/*
 * @providesModule RouteHandler
 *
 */
'use client';

import React, {useCallback, useEffect} from 'react';
import {useRouter} from 'solito/navigation';
import {RouterEventEmitter, RouterEvents} from './SkillzRouter';
import {Platform} from 'react-native';

type Props = {
  onNavigationChange: (
    action: 'push' | 'replace' | 'back' | 'backToRoot' | 'refreshWeb',
    url?: string
  ) => void;
  handleLegacyBack: (cb: () => void) => void;
  /**
   * Optional URL rewriter applied to every push/replace/backToRoot target. WEB-only
   * (the multicade shell surface passes it to remap `/games/19369/*` navigations to
   * their clean top-level / partner URLs — see portal-play SDKProvider). Absent on
   * native, so `router.push`/`replace` receive the URL unchanged and native behavior
   * is byte-identical.
   */
  remapUrl?: (url: string) => string;
};

export default function RouteHandler({onNavigationChange, handleLegacyBack, remapUrl}: Props) {
  const router = useRouter();
  const mapUrl = remapUrl ?? ((url: string) => url);

  const push = useCallback(
    (url: string) => {
      onNavigationChange('push', url);
      router.push(mapUrl(url));
    },
    [router, mapUrl]
  );

  const backToRoot = useCallback(
    (url: string) => {
      onNavigationChange('backToRoot', url);
      router.push(mapUrl(url));
    },
    [router, mapUrl]
  );

  const replace = useCallback(
    (url: string) => {
      onNavigationChange('replace', url);
      router.replace(mapUrl(url));
    },
    [router, mapUrl]
  );
  const back = useCallback(() => {
    onNavigationChange('back');
    handleLegacyBack(() => router.back());
  }, [router]);

  const refreshWeb = useCallback(() => {
    if (Platform.OS === 'web') {
      onNavigationChange('refreshWeb');
    }
  }, [router]);

  useEffect(() => {
    RouterEventEmitter.addListener(RouterEvents.push, push);
    RouterEventEmitter.addListener(RouterEvents.replace, replace);
    RouterEventEmitter.addListener(RouterEvents.back, back);
    RouterEventEmitter.addListener(RouterEvents.backToRoot, backToRoot);
    RouterEventEmitter.addListener(RouterEvents.refreshWeb, refreshWeb);

    return () => {
      RouterEventEmitter.removeAllListeners();
    };
  }, [router]);
  return <></>;
}
